import React, {
  useCallback,
  useMemo,
  ReactNode,
  RefObject,
  useEffect,
} from 'react';
import clsx from 'clsx';
import AddIcon from '@mui/icons-material/Add';
import {
  SelectClasses,
} from '@mui/material/Select';
import {
  EncounterWithCustomCodesViewModel,
  PatientDTO,
} from 'dtos';
import Select from 'core/components/Select';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  formatDate,
} from 'utils/date';
import Tag from 'core/components/Tag';
import isNil from 'lodash/isNil';
import {
  useGetEncountersWithCustomCodesByPatientId,
} from 'views/EMR/hooks/billing';
import {
  useColorsHashMap,
} from 'pages/Dashboard/pages/Encounters/hooks';
import head from 'lodash/head';
import {
  useFormAutoFocus,
} from 'views/EMR/hooks/common';

type Part = 'root' | 'label';

type Props = {
  id: string;
  patient?: PatientDTO;
  encounterId?: number;
  reasonForVisit?: string;
  label: string;
  className?: string;
  classes?: Partial<SelectClasses & Record<Part, string>>;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  onChange: (
    option: EncounterWithCustomCodesViewModel | EncounterWithCustomCodesViewModel[],
  ) => void;
  getLabel?: (option: EncounterWithCustomCodesViewModel, index?: number) => string | ReactNode;
  wrapperRef?: RefObject<HTMLDivElement>;
  showErrorText?: boolean;
  error?: boolean;
  autoSelect?: boolean;
  autoFocus?: boolean;
};

type EncounterTagProps = {
  encounter?: EncounterWithCustomCodesViewModel;
  placeholder?: string;
};

function EncounterTag({
  encounter,
  placeholder,
}: EncounterTagProps) {
  return (isNil(encounter)
    ? <span className="text-gray-500">{placeholder}</span>
    : (
      <div className="w-full flex items-center gap-2">
        {encounter?.encounterId === 0
          ? (
            <>
              <AddIcon className="!w-4 !h-4 !text-gray-500" />
              <strong className="font-semibold">New Encounter</strong>
            </>
          )
          : (
            <>
              <Tag
                className="max-w-[250px] truncate"
                tagName={encounter?.typeOfVisitDescription ?? ''}
              />
              <p className="font-semibold">{formatDate(encounter?.startDateTime ?? '')}</p>
            </>
          )}
      </div>
    )
  );
}

export default function PatientEncounterSelectForPurchase({
  id,
  patient,
  encounterId: selectedEncounterId,
  label,
  className,
  onChange,
  required,
  disabled = false,
  placeholder = 'Select Encounter',
  classes,
  getLabel: _getLabel,
  wrapperRef,
  error,
  showErrorText = false,
  autoSelect = false,
  autoFocus = false,
}: Props) {
  const {
    data = [],
    isLoading,
    isFetching,
  } = useGetEncountersWithCustomCodesByPatientId(patient?.patientId);

  const inputRef = useFormAutoFocus({ enabled: autoFocus && !isLoading });

  const colors = useColorsHashMap();

  const selectedEncounter = useMemo(() => data?.find(
    ({ encounterId }) => selectedEncounterId === encounterId,
  ), [selectedEncounterId, data]);

  const getLabel = useCallback((option: EncounterWithCustomCodesViewModel) => (
    <EncounterTag encounter={option} />
  ), []);

  const options = useMemo(() => [{ encounterId: 0 }, ...data], [data]);

  useEffect(() => {
    if (autoSelect && !isLoading && data.length > 0) {
      onChange(head(data) as EncounterWithCustomCodesViewModel);
    }
  }, [autoSelect, isLoading, data]);

  return (
    <Wrapper
      id={id}
      error={error}
      showErrorText={showErrorText}
      className={classes?.root}
      label={label}
      required={required}
      wrapperRef={wrapperRef}
      labelClasses="font-semibold text-xs text-gray-500"
    >
      <Select<EncounterWithCustomCodesViewModel>
        inputRef={inputRef}
        className={clsx('w-full', className)}
        muiClasses={classes}
        id={id}
        value={selectedEncounter ?? null}
        options={options}
        getValue={(option) => option?.encounterId ?? ''}
        getLabel={getLabel}
        disabled={isFetching || disabled}
        onChange={onChange}
        placeholder={placeholder}
        renderValue={() => (
          isNil(selectedEncounter)
            ? placeholder
            : (
              <div className="flex w-full items-center gap-2 font-semibold">
                <div className={clsx('rounded-full w-3 h-3', colors[selectedEncounter?.typeOfVisitDescription ?? ''])} />
                {formatDate(selectedEncounter?.startDateTime ?? '')}
              </div>
            )
        )}
      />
    </Wrapper>
  );
}
